import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Clock, Truck, User } from "lucide-react";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_MAPS_API_KEY || "";

const haversineDistance = (p1, p2) => {
  const R = 6371e3;
  const dLat = ((p2.latitude - p1.latitude) * Math.PI) / 180;
  const dLon = ((p2.longitude - p1.longitude) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((p1.latitude * Math.PI) / 180) *
    Math.cos((p2.latitude * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const downsamplePoints = (points, minDistanceMeters = 50) => {
  if (!points || points.length === 0) return [];

  const filtered = points.filter(
    (p) =>
      p.latitude && p.longitude &&
      p.latitude !== 0 && p.longitude !== 0 &&
      p.latitude !== -90 && p.longitude !== -180 &&
      (p.accuracy === undefined || p.accuracy <= 200)
  );

  if (filtered.length === 0) return [];

  const result = [filtered[0]];

  for (let i = 1; i < filtered.length; i++) {
    const prev = result[result.length - 1];
    const curr = filtered[i];
    const dist = haversineDistance(prev, curr);

    if (dist >= minDistanceMeters) {
      result.push(curr);
    }
  }

  if (filtered.length > 1) {
    const lastPoint = filtered[filtered.length - 1];
    if (result[result.length - 1] !== lastPoint) {
      result.push(lastPoint);
    }
  }

  return result;
};

const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google?.maps) {
      resolve(window.google.maps);
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google.maps));
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const RealTimeTrackMap = ({ trackingData, trip }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const markerRef = useRef(null);
  const [mapsReady, setMapsReady] = useState(!!window.google?.maps);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (window.google?.maps) {
      setMapsReady(true);
      return;
    }
    loadGoogleMapsScript()
      .then(() => setMapsReady(true))
      .catch(() => setLoadError(true));
  }, []);

  useEffect(() => {
    if (!mapsReady || !mapContainerRef.current || mapInstanceRef.current) return;

    const currentLoc = trackingData?.current_location;
    const hasLocation = currentLoc?.latitude && currentLoc?.longitude && currentLoc.latitude !== -90 && currentLoc.longitude !== -180;
    const initialCenter = hasLocation
      ? { lat: currentLoc.latitude, lng: currentLoc.longitude }
      : { lat: 22.5937, lng: 82.9629 };
    const initialZoom = hasLocation ? 15 : 5;

    mapInstanceRef.current = new window.google.maps.Map(mapContainerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      disableDefaultUI: false,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: true,
      gestureHandling: "greedy",
      minZoom: 4,
      maxZoom: 18,
      restriction: {
        latLngBounds: { north: 38.0, south: 8.0, east: 98.0, west: 68.0 },
        strictBounds: false,
      },
    });
  }, [mapsReady]);

  const pathPoints = useMemo(() => {
    if (!trackingData?.all_latlong) return [];

    // Reverse array to make it chronological (oldest first)
    const chronological = [...trackingData.all_latlong].reverse();
    const totalPoints = chronological.length;
    let minDist = 50;
    if (totalPoints > 50000) minDist = 150;
    else if (totalPoints > 20000) minDist = 100;
    else if (totalPoints > 5000) minDist = 70;

    return downsamplePoints(chronological, minDist);
  }, [trackingData?.all_latlong]);

  useEffect(() => {
    if (!mapInstanceRef.current || !mapsReady) return;

    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
      directionsRendererRef.current = null;
    }
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }

    const polylinePath = pathPoints.map((p) => ({ lat: p.latitude, lng: p.longitude }));

    if (polylinePath.length > 1) {
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
        map: mapInstanceRef.current,
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: "#3b82f6",
          strokeOpacity: 0.85,
          strokeWeight: 2,
        },
      });

      const origin = polylinePath[0];
      const destination = polylinePath[polylinePath.length - 1];

      // Select up to 20 intermediate waypoints for accurate road tracking
      const waypoints = [];
      if (polylinePath.length > 2) {
        const step = (polylinePath.length - 2) / 20;
        for (let i = 1; i <= 20; i++) {
          const index = Math.round(i * step);
          if (index < polylinePath.length - 1) {
            waypoints.push({
              location: polylinePath[index],
              stopover: false,
            });
          }
        }
      }

      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin,
          destination,
          waypoints,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && directionsRendererRef.current) {
            directionsRendererRef.current.setDirections(result);
          } else {
            console.warn("Directions request failed due to " + status);
            if (directionsRendererRef.current) {
              directionsRendererRef.current.setMap(null);
              directionsRendererRef.current = null;
            }
          }
        }
      );
    }

    const currentLoc = trackingData?.current_location;
    if (currentLoc?.latitude && currentLoc?.longitude && currentLoc.latitude !== -90 && currentLoc.longitude !== -180) {
      const isMoving = trackingData?.movement_status === "moving";
      const heading = currentLoc.heading || 0;

      const vehicleLatLng = { lat: currentLoc.latitude, lng: currentLoc.longitude };
      markerRef.current = new window.google.maps.Marker({
        position: vehicleLatLng,
        map: mapInstanceRef.current,
        icon: {
          path: "M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z",
          fillColor: isMoving ? "#22c55e" : "#9ca3af",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
          scale: 1.5,
          rotation: heading,
          anchor: new window.google.maps.Point(12, 12),
        },
        title: trackingData?.vehicle?.vehicle_number || "Vehicle",
      });

      // Always center and zoom in on the truck arrow/current location
      mapInstanceRef.current.setCenter(vehicleLatLng);
      mapInstanceRef.current.setZoom(15);
    }
  }, [pathPoints, trackingData?.current_location, trackingData?.movement_status, mapsReady]);

  const formatSpeed = (speed) => {
    if (!speed && speed !== 0) return "N/A";
    return `${parseFloat(speed).toFixed(0)} km/h`;
  };

  const formatDistance = (meters) => {
    if (!meters && meters !== 0) return "N/A";
    if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
    return `${parseFloat(meters).toFixed(0)} m`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full min-h-[500px] bg-background rounded-xl border border-border">
        <p className="text-sm text-destructive">Failed to load Google Maps</p>
      </div>
    );
  }

  if (!mapsReady) {
    return (
      <div className="flex items-center justify-center h-full min-h-[500px] bg-background rounded-xl border border-border">
        <div className="flex flex-col items-center gap-2">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-muted-foreground">Loading map...</p>
        </div>
      </div>
    );
  }

  const driverName = trackingData?.driver?.user?.username || trackingData?.driver?.name || trip?.driver?.user?.username || trip?.driver?.name || "Unknown";
  const vehicleNumber = trackingData?.vehicle?.vehicle_number || trip?.vehicle?.vehicle_number || "Unknown";
  const currentSpeed = trackingData?.current_speed || trackingData?.current_location?.speed || 0;
  const remainingDist = trackingData?.eta?.remaining_distance;
  const distTraveled = trackingData?.path_coverage?.distance_traveled;
  const movementStatus = trackingData?.movement_status || "unknown";
  const lastTimestamp = trackingData?.current_location?.timestamp;

  const statusColor = {
    moving: "text-emerald-500",
    stopped: "text-amber-500",
    stationary: "text-red-400",
    unknown: "text-muted-foreground",
  };

  return (
    <div className="border border-border rounded-2xl bg-card w-full h-full flex flex-col overflow-hidden">
      <div className="flex-grow relative">
        <div
          ref={mapContainerRef}
          className="w-full h-full rounded-t-2xl"
          style={{ minHeight: "500px" }}
        />

      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-3 border-t border-border bg-card text-xs">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Truck size={13} className="text-muted-foreground" />
            <span className="font-medium text-foreground">{vehicleNumber}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <User size={13} className="text-muted-foreground" />
            <span className="text-foreground">{driverName}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Clock size={12} />
          <span>Last: {formatTime(lastTimestamp)}</span>
        </div>
      </div>
    </div>
  );
};

export default RealTimeTrackMap;